import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CfDnsAccount, CfDnsZoneRef } from '@khc/contracts';
import {
  createCfDnsAccount,
  deleteCfDnsAccount,
  fetchCfDnsAccounts,
  refreshCfDnsAccount,
  updateCfDnsAccount,
} from '../../data/cfDns';
import { action, actionLabel, type ActionRef } from '../../registry/actions';
import { ConfirmAction } from '../../patterns';
import {
  Banner,
  Button,
  Checkbox,
  Drawer,
  Field,
  InlineError,
  Row,
  Stack,
  Table,
  TextInput,
  useToast,
  type ColumnDef,
} from '../../ui';
import { zh, fmt } from '../../i18n/zh';

type DrawerState = {
  mode: 'create' | 'edit';
  account: CfDnsAccount | null;
  name: string;
  token: string;
  enabled: boolean;
  zones: CfDnsZoneRef[];
  selectedZoneIds: string[];
  busy: boolean;
  error: string | null;
};

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * Cloudflare DNS 凭据（设置页「Cloudflare DNS」分段，B 列表对象）：
 * 一行一条凭据（名称 / 账号 / 已启用 Zone / Token 状态），行点开同一抽屉编辑；
 * Zone 用复选列表（不做 Zone→子域名级联）；Token 只写不读。
 */
export function CfDnsAccounts({ createSignal }: { createSignal: number }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const accountsQuery = useQuery({ queryKey: ['cfDns', 'accounts'], queryFn: fetchCfDnsAccounts });
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CfDnsAccount | null>(null);

  const openCreate = () => {
    setDrawer({
      mode: 'create',
      account: null,
      name: '',
      token: '',
      enabled: true,
      zones: [],
      selectedZoneIds: [],
      busy: false,
      error: null,
    });
  };

  useEffect(() => {
    if (createSignal > 0) openCreate();
  }, [createSignal]);

  const openEdit = (account: CfDnsAccount) => {
    setDrawer({
      mode: 'edit',
      account,
      name: account.name,
      token: '',
      enabled: account.enabled,
      zones: account.zones,
      selectedZoneIds: account.enabledZoneIds,
      busy: false,
      error: null,
    });
  };

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['cfDns'] });
  };

  const submit = async () => {
    if (!drawer) return;
    if (!drawer.name.trim()) {
      setDrawer((prev) => (prev ? { ...prev, error: zh.cfDns.nameRequired } : prev));
      return;
    }
    setDrawer((prev) => (prev ? { ...prev, busy: true, error: null } : prev));
    try {
      if (drawer.mode === 'create') {
        if (!drawer.token.trim()) {
          setDrawer((prev) => (prev ? { ...prev, busy: false, error: zh.cfDns.tokenRequired } : prev));
          return;
        }
        const result = await createCfDnsAccount({ name: drawer.name.trim(), token: drawer.token.trim() });
        await invalidate();
        toast.show({ message: zh.cfDns.createdToast });
        // 拉取成功后切到编辑态，让主人取消勾选不需要的 Zone 再保存
        setDrawer({
          mode: 'edit',
          account: result.account,
          name: result.account.name,
          token: '',
          enabled: result.account.enabled,
          zones: result.zones,
          selectedZoneIds: result.account.enabledZoneIds,
          busy: false,
          error: null,
        });
        return;
      }
      await updateCfDnsAccount(drawer.account!.id, {
        name: drawer.name.trim(),
        enabled: drawer.enabled,
        enabledZoneIds: drawer.selectedZoneIds,
      });
      await invalidate();
      toast.show({ message: zh.common.saved });
      setDrawer(null);
    } catch (error) {
      setDrawer((prev) =>
        prev ? { ...prev, busy: false, error: errorText(error, zh.cfDns.loadError) } : prev,
      );
    }
  };

  const refresh = async () => {
    if (!drawer?.account) return;
    setDrawer((prev) => (prev ? { ...prev, busy: true, error: null } : prev));
    try {
      const result = await refreshCfDnsAccount(drawer.account.id);
      setDrawer((prev) =>
        prev
          ? {
              ...prev,
              busy: false,
              zones: result.zones,
              selectedZoneIds: result.account.enabledZoneIds,
            }
          : prev,
      );
      await invalidate();
      toast.show({ message: zh.cfDns.refreshOk });
    } catch (error) {
      setDrawer((prev) =>
        prev ? { ...prev, busy: false, error: errorText(error, zh.cfDns.loadError) } : prev,
      );
    }
  };

  const remove = async (account: CfDnsAccount) => {
    await deleteCfDnsAccount(account.id);
    await invalidate();
    toast.show({ message: zh.cfDns.deletedToast });
    setDeleteTarget(null);
  };

  const columns: ColumnDef<CfDnsAccount>[] = [
    { key: 'name', type: 'name', header: zh.cfDns.colName, value: (item) => item.name },
    {
      key: 'account',
      type: 'text',
      header: zh.cfDns.colAccount,
      priority: 'low',
      value: (item) => item.accountName ?? '—',
    },
    {
      key: 'zones',
      type: 'text',
      header: zh.cfDns.colZones,
      value: (item) => `${item.enabledZoneIds.length}/${item.zones.length}`,
    },
    {
      key: 'token',
      type: 'status',
      header: zh.cfDns.colToken,
      status: (item) => ({
        tone: item.hasToken ? 'ok' : 'warn',
        label: item.hasToken ? zh.cfDns.tokenOk : zh.cfDns.tokenMissing,
      }),
    },
    { key: 'actions', type: 'actions', header: '' },
  ];

  const handleAction = (ref: ActionRef, account: CfDnsAccount) => {
    if (ref.id === 'cfDns.account.update') openEdit(account);
    if (ref.id === 'cfDns.account.delete') setDeleteTarget(account);
  };

  return (
    <Stack gap={4}>
      <Banner tone="info" title={zh.cfDns.noticeTitle} description={zh.cfDns.notice} />

      {accountsQuery.error ? (
        <Banner
          tone="danger"
          title={zh.cfDns.loadError}
          description={(accountsQuery.error as Error).message}
          action={
            <Button label={zh.common.retry} size="sm" onClick={() => void accountsQuery.refetch()} />
          }
        />
      ) : null}

      <Table
        rows={accountsQuery.isLoading ? undefined : (accountsQuery.data ?? [])}
        columns={columns}
        rowKey={(item) => item.id}
        objectLabel={zh.cfDns.colName}
        empty={{
          icon: 'globe',
          title: zh.cfDns.emptyTitle,
          description: zh.cfDns.emptyHint,
          action: (
            <Button
              label={actionLabel(action('cfDns.account.create'))}
              variant="primary"
              icon="plus"
              onClick={openCreate}
            />
          ),
        }}
        rowActions={(item) => [action('cfDns.account.update'), action('cfDns.account.delete')]}
        onRowAction={(ref, item) => handleAction(ref, item)}
        onRowOpen={openEdit}
      />

      {drawer ? (
        <Drawer
          open
          title={drawer.mode === 'create' ? zh.cfDns.addTitle : zh.cfDns.editTitle}
          context={zh.cfDns.context}
          dirty={drawer.mode === 'create' ? drawer.name.trim().length > 0 : false}
          onClose={() => setDrawer(null)}
          footer={
            <Row gap={2}>
              {drawer.mode === 'edit' && drawer.account && drawer.account.id !== 'legacy' ? (
                <Button
                  label={zh.cfDns.refresh}
                  size="sm"
                  loading={drawer.busy}
                  onClick={() => void refresh()}
                />
              ) : null}
              <Button label={zh.common.cancel} onClick={() => setDrawer(null)} />
              <Button
                label={drawer.mode === 'create' ? zh.cfDns.add : zh.common.save}
                variant="primary"
                loading={drawer.busy}
                onClick={() => void submit()}
              />
            </Row>
          }
        >
          <Stack gap={4}>
            <Field id="cf-account-name" label={zh.cfDns.nameLabel} help={zh.cfDns.nameHelp}>
              <TextInput
                id="cf-account-name"
                value={drawer.name}
                onChange={(value) => setDrawer((prev) => (prev ? { ...prev, name: value } : prev))}
              />
            </Field>

            {drawer.mode === 'create' ? (
              <Field id="cf-account-token" label={zh.cfDns.tokenLabel} help={zh.cfDns.tokenHint}>
                <TextInput
                  id="cf-account-token"
                  value={drawer.token}
                  mono
                  placeholder="cf-api-token…"
                  onChange={(value) => setDrawer((prev) => (prev ? { ...prev, token: value } : prev))}
                />
              </Field>
            ) : (
              <Field id="cf-account-token" label={zh.cfDns.tokenLabel} help={zh.cfDns.tokenKeepHint}>
                <TextInput id="cf-account-token" value="" mono readOnly placeholder={zh.cfDns.tokenReadonly} onChange={() => {}} />
              </Field>
            )}

            <Checkbox
              checked={drawer.enabled}
              onChange={(checked) => setDrawer((prev) => (prev ? { ...prev, enabled: checked } : prev))}
              label={zh.cfDns.enableLabel}
              description={zh.cfDns.enableHint}
            />

            <Field id="cf-account-zones" label={zh.cfDns.zoneListLabel} help={zh.cfDns.zoneListHelp}>
              {drawer.zones.length === 0 ? (
                <span className="ui-muted">{zh.cfDns.zoneListEmpty}</span>
              ) : (
                <Stack gap={2}>
                  {drawer.zones.map((zone) => (
                    <Checkbox
                      key={zone.id}
                      checked={drawer.selectedZoneIds.includes(zone.id)}
                      onChange={(checked) =>
                        setDrawer((prev) =>
                          prev
                            ? {
                                ...prev,
                                selectedZoneIds: checked
                                  ? [...prev.selectedZoneIds, zone.id]
                                  : prev.selectedZoneIds.filter((id) => id !== zone.id),
                              }
                            : prev,
                        )
                      }
                      label={zone.name}
                    />
                  ))}
                </Stack>
              )}
            </Field>

            {drawer.mode === 'create' ? (
              <span className="ui-muted">{zh.cfDns.createHint}</span>
            ) : null}
            {drawer.error ? <InlineError message={drawer.error} /> : null}
          </Stack>
        </Drawer>
      ) : null}

      {deleteTarget ? (
        <ConfirmAction
          open
          action={action('cfDns.account.delete')}
          title={fmt(zh.cfDns.deleteTitle, { name: deleteTarget.name })}
          body={zh.cfDns.deleteBody}
          confirmLabel={zh.cfDns.deleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => remove(deleteTarget)}
        />
      ) : null}
    </Stack>
  );
}
